import "./ServicesPills.scss";

import { KaboodleToaster } from "components/common";
import { Tooltip } from "components/Tooltip/Tooltip";
import { FM_SERVICE_TYPES } from "constants/SettingsConstants";
import type { MouseEvent } from "react";
import { useNavigate } from "react-router-dom";
import type { ServiceType } from "types/DataTypes";
import { getVerboseServiceStatus } from "utils/format-utils";

type ServicesPillsProps = {
  services: ServiceType[];
  fileId?: number;
};

const ServicesPills = ({ services, fileId }: ServicesPillsProps) => {
  const navigate = useNavigate();

  // separate future medical services from other services
  const futureMedicalServices = services.filter((s) =>
    FM_SERVICE_TYPES.includes(s.type),
  );
  const otherServices = services.filter(
    (s) => !FM_SERVICE_TYPES.includes(s.type),
  );

  const handleServiceClick = (service: ServiceType) => {
    // check if service is unprocessed or workspace_url is not available
    if (!service?.status || service.status === "U" || !service?.workspace_url) {
      KaboodleToaster.info("Open File to access this service");
      return;
    }

    if (service?.workspace_url) {
      navigate(service.workspace_url);
    }
  };

  const handleServiceContextMenu = (
    service: ServiceType,
    e: MouseEvent<HTMLDivElement>,
  ) => {
    e.preventDefault();

    // check if service is unprocessed or workspace_url is not available
    if (!service?.status || service.status === "U" || !service?.workspace_url) {
      KaboodleToaster.info("Open File to access this service");
      return;
    }

    if (service?.workspace_url) {
      window.open(service.workspace_url, "_blank");
    }
  };

  const handleFMClick = () => {
    // check if any FM service has a workspace URL or if all are unprocessed
    const hasAccessibleService = futureMedicalServices.some(
      (s) => s.status !== "U" && s.workspace_url,
    );

    if (!hasAccessibleService) {
      KaboodleToaster.info("Open File to access Future Medical");
      return;
    }

    // navigate to FM overview if we have a file ID
    if (fileId) {
      const url = `/future-medical/overview?file_id=${fileId}`;
      navigate(url);
    }
  };

  const handleFMContextMenu = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();

    // check if any FM service has a workspace URL or if all are unprocessed
    const hasAccessibleService = futureMedicalServices.some(
      (s) => s.status !== "U" && s.workspace_url,
    );

    if (!hasAccessibleService) {
      KaboodleToaster.info("Open File to access Future Medical");
      return;
    }

    // navigate to FM overview if we have a file ID
    if (fileId) {
      const url = `/future-medical/overview?file_id=${fileId}`;
      window.open(url, "_blank");
    }
  };

  // count services by type to determine if we need to show dates
  const serviceTypeCounts = services.reduce(
    (acc, service) => {
      acc[service.type] = (acc[service.type] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  const getTooltipContent = (service: ServiceType) => {
    const verboseType = service.type_verbose || service.type;
    const status = service.status
      ? getVerboseServiceStatus(service.status)
      : "Unknown";

    // if there are multiple services of this type, include the created date
    const hasDuplicates = serviceTypeCounts[service.type] > 1;
    if (hasDuplicates && service.created) {
      const createdDate = new Date(service.created).toLocaleDateString();
      return `${verboseType} - ${status} - ${createdDate}`;
    }

    return `${verboseType} - ${status}`;
  };

  const getFMTooltipContent = () => {
    // list all FM services with their statuses
    return futureMedicalServices
      .map((service) => {
        const verboseType = service.type_verbose || service.type;
        const status = service.status
          ? getVerboseServiceStatus(service.status)
          : "Unknown";

        // if there are duplicates of this type, include the date
        const hasDuplicates = serviceTypeCounts[service.type] > 1;
        if (hasDuplicates && service.created) {
          const createdDate = new Date(service.created).toLocaleDateString();
          return `${verboseType} - ${status} - ${createdDate}`;
        }

        return `${verboseType} - ${status}`;
      })
      .join("<br/>");
  };

  const getFMStatus = () => {
    // complete only if all FM services are complete
    if (futureMedicalServices.every((s) => s.status === "C")) {
      return "C";
    }
    // if any are in progress, show in progress
    if (futureMedicalServices.some((s) => s.status === "IN")) {
      return "IN";
    }
    // if any are pending, show pending
    if (futureMedicalServices.some((s) => s.status === "P")) {
      return "P";
    }
    // otherwise unprocessed
    return "U";
  };

  const getStatusClass = (status?: string) => {
    switch (status) {
      case "U":
        return "unprocessed";
      case "P":
        return "pending";
      case "IN":
        return "in-progress";
      case "C":
        return "complete";
      default:
        return "";
    }
  };

  if (!services || services.length === 0) {
    return <span>Not Available</span>;
  }

  const fmStatus = getFMStatus();

  return (
    <div className="services-pills">
      {/* future medical pill (if any FM services exist) */}
      {futureMedicalServices.length > 0 && (
        <Tooltip content={getFMTooltipContent()} place="top" html={true}>
          <div
            className={`service-pill ${getStatusClass(fmStatus)} ${
              futureMedicalServices.some(
                (s) => s.status !== "U" && s.workspace_url,
              )
                ? "clickable"
                : "disabled"
            }`}
            onClick={handleFMClick}
            onContextMenu={handleFMContextMenu}
          >
            Future Medical
          </div>
        </Tooltip>
      )}

      {/* other services */}
      {otherServices.map((service) => (
        <Tooltip
          key={service.id}
          content={getTooltipContent(service)}
          place="top"
        >
          <div
            className={`service-pill ${getStatusClass(service.status)} ${
              service.workspace_url && service.status !== "U"
                ? "clickable"
                : "disabled"
            }`}
            onClick={() => handleServiceClick(service)}
            onContextMenu={(e) => handleServiceContextMenu(service, e)}
          >
            {service.type}
          </div>
        </Tooltip>
      ))}
    </div>
  );
};

export default ServicesPills;
